import { useMemo, useState } from "react";
import { Box } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import APITokenRow from "./APITokenRow";

const GRID_COLS_ALL = "minmax(120px,1fr) minmax(160px,2fr) minmax(130px,1.5fr) minmax(260px,3fr) minmax(90px,1fr) minmax(360px,3.5fr) minmax(105px,1.2fr) minmax(140px,1.4fr) 56px";
const GRID_COLS_SCOPED = "minmax(120px,1fr) minmax(160px,2fr) minmax(260px,3fr) minmax(90px,1fr) minmax(360px,3.5fr) minmax(105px,1.2fr) minmax(140px,1.4fr) 56px";
const MIN_WIDTH_ALL = 1540;
const MIN_WIDTH_SCOPED = 1360;

const buildCols = (showAccountCol) => [
  { label: "Type", key: "token_type" },
  { label: "API Token Name", key: "name" },
  ...(showAccountCol ? [{ label: "Account", key: "account" }] : []),
  { label: "Assigned Workflows", key: "assignedWorkflows" },
  { label: "Status",             key: "status" },
  { label: "Token",              key: "token" },
  { label: "Created By",         key: "created_by" },
  { label: "Date",               key: "date" },
  { label: "Actions",            key: null },
];

function getSortVal(token, key) {
  if (key === "assignedWorkflows") {
    return (token.assignedWorkflows ?? [])
      .map((wf) => `${wf.workflowGroup}/${wf.workflowName}`)
      .join(", ")
      .toLowerCase();
  }
  return String(token[key] ?? "").toLowerCase();
}

export default function APITokenTable({ tokens, showAccountCol, onEdit, onDelete, onDisable, onEnable, onHistory }) {
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  const handleSort = (key) => {
    if (!key) return;
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const sorted = useMemo(
    () =>
      [...tokens].sort((a, b) => {
        const cmp = getSortVal(a, sortKey).localeCompare(getSortVal(b, sortKey));
        return sortDir === "asc" ? cmp : -cmp;
      }),
    [tokens, sortKey, sortDir],
  );

  const gridCols = showAccountCol ? GRID_COLS_ALL : GRID_COLS_SCOPED;
  const minWidth = showAccountCol ? MIN_WIDTH_ALL : MIN_WIDTH_SCOPED;
  const cols = buildCols(showAccountCol);

  return (
    <Box
      sx={{
        border: "1px solid #d5d9e0",
        borderRadius: 2,
        overflowX: "auto",
        bgcolor: "#f6f7fa",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.65)",
      }}
    >
      <Box sx={{ minWidth }}>
        {/* Header row */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: gridCols,
            px: 1.5,
            py: 1.25,
            fontWeight: 700,
            fontSize: "0.8rem",
            letterSpacing: 0.2,
            textTransform: "uppercase",
            color: "#5f6878",
            borderBottom: "1px solid #d5d9e0",
            bgcolor: "#eceff3",
          }}
        >
          {cols.map(({ label, key }) => (
            <Box
              key={label}
              onClick={() => handleSort(key)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.4,
                cursor: key ? "pointer" : "default",
                userSelect: "none",
                "&:hover": key ? { color: "#3b4455" } : {},
              }}
            >
              {label}
              {key && (
                sortKey === key
                  ? sortDir === "asc"
                    ? <ArrowUpwardIcon sx={{ fontSize: 13, opacity: 0.85 }} />
                    : <ArrowDownwardIcon sx={{ fontSize: 13, opacity: 0.85 }} />
                  : <UnfoldMoreIcon sx={{ fontSize: 14, opacity: 0.4 }} />
              )}
            </Box>
          ))}
        </Box>

        {/* Data rows */}
        {sorted.map((token) => (
          <APITokenRow
            key={token.id}
            token={token}
            showAccountCol={showAccountCol}
            gridCols={gridCols}
            onEdit={onEdit}
            onDelete={onDelete}
            onDisable={onDisable}
            onEnable={onEnable}
            onHistory={onHistory}
          />
        ))}

        {!sorted.length && (
          <Box sx={{ p: 2, color: "#7a8393" }}>No API Tokens Found</Box>
        )}
      </Box>
    </Box>
  );
}
